<template>
  <div class="min-h-screen bg-gray-50">
    <!-- 复用Header组件 -->
    <Header />

    <!-- 主要内容 -->
    <div class="container mx-auto py-6 pb-16">
      <!-- 面包屑导航 -->
      <div class="mb-6">
        <div class="flex items-center text-sm bg-white px-4 py-3 rounded-lg shadow-sm">
          <router-link to="/" class="text-gray-500 hover:text-blue-600">网站首页</router-link>
          <span class="mx-2 text-gray-400">/</span>
          <router-link to="/notices" class="text-gray-500 hover:text-blue-600">协会动态</router-link>
          <span class="mx-2 text-gray-400">/</span>
          <span class="text-blue-600 font-medium">{{ activeType }}</span>
        </div>
      </div>

      <div class="flex flex-col lg:flex-row gap-8">
        <!-- 左侧分类 -->
        <div class="lg:w-1/4">
          <div class="mb-6">
            <h3 class="sidebar-title mb-4">{{ activeType }}</h3>
            <div class="bg-white p-4 rounded-lg shadow-sm">
              <ul class="space-y-2">
                <li v-for="category in categories" :key="category.id">
                  <router-link
                    :to="`/notices?category=${encodeURIComponent(category.secondLevelModule)}`"
                    class="block py-2 px-3 hover:bg-gray-50 rounded-md transition-colors"
                    :class="activeType === category.secondLevelModule ? 'bg-blue-50 text-blue-600 font-medium' : 'text-gray-700'"
                  >
                    {{ category.secondLevelModule }}
                  </router-link>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- 右侧通知列表 -->
        <div class="lg:w-3/4 min-h-[400px]">
          <!-- 通知列表 -->
          <div class="space-y-4 h-full">
            <div v-if="loading" class="flex items-center justify-center h-full text-gray-500">加载中...</div>
            <div v-else-if="notices.length === 0" class="flex items-center justify-center h-full text-gray-500">暂无数据</div>
            <div
              v-else
              v-for="notice in notices"
              :key="notice.id"
              class="notice-item p-4 rounded-lg bg-white shadow-sm"
            >
              <div class="flex flex-col md:flex-row gap-4">
                <div class="md:w-1/4 notice-image h-32 md:h-auto bg-gray-100 rounded-md">
                  <div class="flex items-center justify-center h-full text-gray-500 text-sm">
                      <img :src="getFullImageUrl(notice.coverImageUrl)" :alt="notice.title" class="w-full h-full object-cover" @error="handleImageError" />
                  </div>
                </div>
                <div class="md:w-3/4">
                  <router-link :to="'/article/' + notice.id" class="block">
                    <h3 class="notice-title text-lg mb-2 hover:text-blue-600 transition-colors">
                      {{ notice.title }}
                    </h3>
                  </router-link>
                  <p class="text-gray-600 text-sm mb-3 line-clamp-2">
                    {{ notice.summary }}
                  </p>
                  <div class="notice-date text-gray-500 text-sm">
                    {{ notice.date }}
                  </div>
                </div>
              </div>
            </div>
            <!-- 分页 -->
            <div v-if="notices.length > 0" class="flex justify-center mt-8">
              <div class="flex items-center space-x-2">
                <button
                  class="py-2 px-4 border border-gray-300 rounded-md disabled:opacity-50 bg-white hover:bg-gray-50 transition-colors"
                  :disabled="currentPage === 1"
                  @click="prevPage"
                >
                  <i class="fa fa-chevron-left mr-1"></i> 上一页
                </button>
                <button
                  v-for="page in visiblePages"
                  :key="page"
                  class="py-2 px-4 rounded-md transition-colors"
                  :class="page === '...' ? 'cursor-default border-none bg-transparent' : (currentPage === page ? 'bg-blue-600 text-white' : 'border border-gray-300 bg-white hover:bg-gray-50')"
                  :disabled="page === '...'"
                  @click="page !== '...' && goToPage(page)"
                >
                  {{ page }}
                </button>
                <button
                  class="py-2 px-4 border border-gray-300 rounded-md bg-white hover:bg-gray-50 transition-colors"
                  :disabled="currentPage === totalPages"
                  @click="nextPage"
                >
                  下一页 <i class="fa fa-chevron-right ml-1"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <AppFooter />
  </div>
</template>

<script>
import Header from '../components/Header.vue'
import AppFooter from '../components/Footer.vue'
import { api } from '../api'
import {CURRENT_CONFIG} from "@/api/config";

export default {
  name: 'NoticeList',
  components: {
    Header,
    AppFooter
  },
  data() {
    return {
      activeType: 'notice',
      currentPage: 1,
      totalPages: 1,
      pageSize: 10,
      notices: [],
      categories: [],
      loading: false,
      BASE_URL: CURRENT_CONFIG.BASE_URL
    }
  },
  computed: {
    visiblePages() {
      const total = this.totalPages
      const current = this.currentPage
      const maxVisible = 5
      if (total <= maxVisible) {
        return Array.from({ length: total }, (_, i) => i + 1)
      }
      const pages = []
      if (current <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i)
        pages.push('...')
        pages.push(total)
      } else if (current >= total - 2) {
        pages.push(1)
        pages.push('...')
        for (let i = total - 3; i <= total; i++) pages.push(i)
      } else {
        pages.push(1)
        pages.push('...')
        for (let i = current - 1; i <= current + 1; i++) pages.push(i)
        pages.push('...')
        pages.push(total)
      }
      return pages
    }
  },
  async mounted() {
    await this.loadCategories()
    this.setActiveTypeFromRoute()
    await this.loadNotices()
  },
  watch: {
    '$route.query.category'() {
      this.setActiveTypeFromRoute()
      this.currentPage = 1
      this.loadNotices()
    },
    activeType() {
      this.currentPage = 1
      this.loadNotices()
    }
  },
  methods: {
    async loadCategories() {
      try {
        const categories = await api.getCategories()
        const noticeCategories = categories.filter(cat => cat.firstLevelModule === '协会动态')
        this.categories = noticeCategories
      } catch (error) {
        console.error('加载分类失败:', error)
      }
    },
    setActiveTypeFromRoute() {
      const categoryFromRoute = this.$route.query.category
      if (categoryFromRoute) {
        this.activeType = categoryFromRoute
      } else if (this.categories.length > 0) {
        this.activeType = this.categories[0].secondLevelModule
      }
    },
    async loadNotices() {
      try {
        this.loading = true
        const category = this.categories.find(cat => cat.secondLevelModule === this.activeType)
        if (!category) {
          this.notices = []
          return
        }

        const result = await api.getArticles(category.id, '', this.currentPage, this.pageSize)
        this.notices = result.rows.map(article => ({
          id: article.id,
          title: article.title || '暂无标题',
          summary: article.summary || (article.content ? article.content.substring(0, 150) + '...' : null) || '暂无摘要',
          date: this.formatDate(article.publishedTime),
          coverImageUrl: article.coverImageUrl
        }))

        const total = result.total || 0
        this.totalPages = Math.ceil(total / this.pageSize)
      } catch (error) {
        console.error('加载通知列表失败:', error)
        this.notices = []
      } finally {
        this.loading = false
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return '未知时间'
      const date = new Date(dateStr)
      return date.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--
        this.loadNotices()
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++
        this.loadNotices()
      }
    },
    goToPage(page) {
      this.currentPage = page
      this.loadNotices()
    },
    getFullImageUrl(path) {
      if (!path) return require('../assets/default_logo.jpg')
      if (path.startsWith('http')) return path
      return this.BASE_URL + path
    },
    handleImageError(event) {
      event.target.src = require('../assets/default_logo.jpg')
    }
  }
}
</script>

<style scoped>
.sidebar-title {
  color: #1f2937;
  font-weight: bold;
  font-size: 18px;
  border-left: 4px solid #3b82f6;
  padding-left: 12px;
}

.notice-item {
  border-bottom: 1px solid #e5e7eb;
  transition: all 0.3s ease;
}

.notice-item:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  transform: translateY(-1px);
}

.notice-title {
  color: #1f2937;
  font-weight: 500;
}
</style>
